import type { Port, Ship, ShipHistoryForm, ShipInfoVo } from "@zhihaoscm/entity";
import { PORT_TYPE, SHIP_STATUS, SHIP_TYPE } from "@zhihaoscm/meta";
import type { ReactNode } from "react";
import type { VehicleQueryLimitInfo } from "@/api/shipService";
import { useFullAreaName } from "@/runtime/areaData";

const EMPTY_VALUE = "--";

/** 还原 Vue 搜索结果条目，避免通用 List 改变船名、MMSI 和图标布局。 */
function ShipResultItem({ ship, active, onSelect }: { ship: Ship; active?: boolean; onSelect: (ship: Ship) => void }) {
    return (
        <button className={`find-ship-result-item${active ? " active" : ""}`} type="button" onClick={() => onSelect(ship)}>
            <span className="find-ship-result-icon"><i className="iconfont icon-boat" /></span>
            <span className="find-ship-result-body">
                <strong>{ship.cnname ? `${ship.cnname} / ` : ""}{ship.name}</strong>
                <span><em>MMSI {ship.id}</em><em>类型 {SHIP_TYPE.getIntroByCode(ship.type) || EMPTY_VALUE}</em></span>
            </span>
        </button>
    );
}

/** 查船结果面板与 Vue shipList.vue 保持相同的标题、空态和条目结构。 */
export function FindShipResultList({ ships, selectedId, onSelect }: { ships: Ship[]; selectedId?: string; onSelect: (ship: Ship) => void }) {
    return (
        <div className="find-location-panel find-ship-result-list">
            {ships.length ? <>
                <div className="find-ship-panel-title">船舶</div>
                {ships.map(ship => <ShipResultItem key={ship.id} ship={ship} active={ship.id === selectedId} onSelect={onSelect} />)}
            </> : <div className="find-ship-empty">没有查询到相关船舶或港口</div>}
        </div>
    );
}

/** 港口结果使用 Vue shipList.vue 的胶囊类型、行政区和图标，不借用通用列表。 */
function PortResultItem({ port, onSelect }: { port: Port; onSelect: (port: Port) => void }) {
    const areaName = useFullAreaName(port.provinceCode, port.cityCode, port.regionCode);
    const icon = port.type === PORT_TYPE.HARBOR.code ? "icon-gangkou" : "icon-berth";
    return <button className="find-ship-result-item" type="button" onClick={() => onSelect(port)}>
        <span className="find-ship-history-kind"><i className={`iconfont ${icon}`} /><em>{PORT_TYPE.getIntroByCode(port.type) || EMPTY_VALUE}</em></span>
        <span className="find-ship-result-body"><strong>{port.shortName}</strong><span><em>{areaName || port.name || port.address || ""}</em></span></span>
    </button>;
}

/** 搜索结果同时展示船舶和港口；两类同时存在时各最多展示六条，与 Vue 计算属性一致。 */
export function FindShipAndPortResultList({ ships, ports, selectedId, onSelectShip, onSelectPort }: {
    ships: Ship[];
    ports: Port[];
    selectedId?: string;
    onSelectShip: (ship: Ship) => void;
    onSelectPort: (port: Port) => void;
}) {
    const visibleShips = ports.length ? ships.slice(0, 6) : ships;
    const visiblePorts = ships.length ? ports.slice(0, 6) : ports;
    return <div className="find-location-panel find-ship-result-list">
        {visibleShips.length ? <><div className="find-ship-panel-title">船舶</div>{visibleShips.map(ship => <ShipResultItem key={ship.id} ship={ship} active={ship.id === selectedId} onSelect={onSelectShip} />)}</> : null}
        {visiblePorts.length ? <><div className="find-ship-panel-title port-title">港口</div>{visiblePorts.map(port => <PortResultItem key={port.id} port={port} onSelect={onSelectPort} />)}</> : null}
        {!visibleShips.length && !visiblePorts.length ? <div className="find-ship-empty">没有查询到相关船舶或港口</div> : null}
    </div>;
}

/** 历史面板复用旧页的船舶胶囊图标、删除入口和空态文案。 */
export function FindShipHistory({ history, onClear, onDelete, onSelect }: { history: Ship[]; onClear: () => void; onDelete: (ship: Ship) => void; onSelect: (ship: Ship) => void }) {
    return (
        <div className="find-location-panel find-ship-history">
            <div className="find-location-history-head"><strong>历史搜索</strong><button type="button" onClick={onClear}>清空</button></div>
            <div className="find-ship-history-content">
                {history.length ? history.map(ship => (
                    <div className="find-ship-history-item" key={ship.id}>
                        <button className="find-ship-history-main" type="button" onClick={() => onSelect(ship)}>
                            <span className="find-ship-history-kind"><i className="iconfont icon-boat" /><em>船舶</em></span>
                            <span className="find-ship-result-body">
                                <strong>{ship.cnname ? `${ship.cnname} / ` : ""}{ship.name}</strong>
                                <span><em>MMSI {ship.id}</em><em>类型 {SHIP_TYPE.getIntroByCode(ship.type) || EMPTY_VALUE}</em></span>
                            </span>
                        </button>
                        <button className="find-ship-history-delete" aria-label={`删除${ship.cnname || ship.name}`} type="button" onClick={() => onDelete(ship)}><i className="iconfont icon-close" /></button>
                    </div>
                )) : <div className="find-ship-empty">没有搜索历史</div>}
            </div>
        </div>
    );
}

/** 历史接口返回船舶和港口联合结构，名称字段是旧页区分类型的唯一合同。 */
export function FindShipMixedHistory({ history, usage, showUsage, onClear, onDelete, onSelectShip, onSelectPort, onToggleUsage, onUpgrade }: {
    history: ShipHistoryForm[];
    usage: VehicleQueryLimitInfo | null;
    showUsage: boolean;
    onClear: () => void;
    onDelete: (item: ShipHistoryForm) => void;
    onSelectShip: (item: ShipHistoryForm) => void;
    onSelectPort: (item: ShipHistoryForm) => void;
    onToggleUsage: () => void;
    onUpgrade: () => void;
}) {
    return <div className="find-location-panel find-ship-history">
        <div className="find-location-history-head"><strong>历史搜索</strong><span className="find-history-actions"><button type="button" onClick={onClear}>清空</button>{usage ? <button className="find-usage-info" aria-label="查看今日查船用量" type="button" onClick={onToggleUsage}><i className="iconfont icon-tishi" /></button> : null}</span></div>
        <div className="find-ship-history-content">{history.length ? history.map(item => {
            const isShip = Boolean(item.name || item.cnname);
            const portItem = item as ShipHistoryForm & Partial<Port>;
            const icon = item.type === PORT_TYPE.HARBOR.code ? "icon-gangkou" : "icon-berth";
            return <div className="find-ship-history-item" key={`${isShip ? "ship" : "port"}-${item.id}`}>
                <button className="find-ship-history-main" type="button" onClick={() => isShip ? onSelectShip(item) : onSelectPort(item)}>
                    <span className="find-ship-history-kind"><i className={`iconfont ${isShip ? "icon-boat" : icon}`} /><em>{isShip ? "船舶" : PORT_TYPE.getIntroByCode(item.type) || EMPTY_VALUE}</em></span>
                    <span className="find-ship-result-body"><strong>{isShip ? `${item.cnname ? `${item.cnname} / ` : ""}${item.name || ""}` : item.shortName}</strong><span>{isShip ? <><em>MMSI {item.id}</em><em>类型 {SHIP_TYPE.getIntroByCode(item.type) || EMPTY_VALUE}</em></> : <em>{[portItem.provinceCode, portItem.cityCode, portItem.regionCode].filter(Boolean).join("")}</em>}</span></span>
                </button>
                <button className="find-ship-history-delete" aria-label={`删除${item.cnname || item.name || item.shortName}`} type="button" onClick={() => onDelete(item)}><i className="iconfont icon-close" /></button>
            </div>;
        }) : <div className="find-ship-empty">没有搜索历史</div>}</div>
        {showUsage && usage ? <VehicleQueryUsageCard usage={usage} onUpgrade={onUpgrade} /> : null}
    </div>;
}

/** 查船用量卡逐字段对应 Vue vehicleQueryUsageCard.vue。 */
export function VehicleQueryUsageCard({ usage, onUpgrade }: { usage: VehicleQueryLimitInfo; onUpgrade: () => void }) {
    const highest = usage.highestLevel === 1 || usage.memberLevel === 3 || usage.memberLevelName?.includes("高级");
    const nonMember = usage.memberLevelName?.includes("非会员") || usage.memberLevelName?.includes("普通用户") || (!usage.memberLevelName?.includes("会员") && (!usage.memberLevel || usage.memberLevel === 1));
    return <div className="vehicle-query-usage-card">
        <div className="usage-title-row"><span className="usage-title">今日查询用量</span><span className="usage-reset-tip">每日0点重置</span></div>
        {usage.hasUnLimit === 1 ? <div className="usage-count unlimited">不限次</div> : <div className="usage-count"><span>剩余<strong className={usage.remainingTimes <= 0 ? "danger" : "primary"}>{usage.remainingTimes}</strong>/共{usage.totalTimes}次</span><small>({usage.remainingTimes > 0 ? <>已用<b>{usage.usedTimes}</b>次</> : "查询次数已用完"})</small></div>}
        {!highest ? <button className="upgrade-btn" type="button" onClick={onUpgrade}>{nonMember ? "开通会员，享更多次数" : "升级会员，享更多次数"}</button> : null}
    </div>;
}

/** 详情字段单元，严格对应 Vue shipDetail.vue 的两列网格。 */
function DetailItem({ label, value, wide }: { label: string; value?: ReactNode; wide?: boolean }) {
    return <div className={`find-ship-detail-item${wide ? " wide" : ""}`}><span>{label}</span><strong>{value === 0 ? 0 : value || EMPTY_VALUE}</strong></div>;
}

/** 船舶详情面板，不使用 Ant Descriptions，以保持旧页网格和按钮尺寸。 */
export function FindShipDetail({ ship, fromList, onBack, onClose, onTrack, onFollow }: { ship: ShipInfoVo; fromList: boolean; onBack: () => void; onClose: () => void; onTrack: () => void; onFollow: () => void }) {
    return (
        <div className="find-location-detail">
            <div className="find-ship-detail-head">
                <span>{fromList ? <button aria-label="返回列表" type="button" onClick={onBack}><i className="iconfont icon-right" /></button> : null}<strong>{ship.cnname}{ship.cnname && ship.name ? " / " : ""}{ship.name}</strong></span>
                <span><i className="iconfont icon-up" /><button aria-label="关闭详情" type="button" onClick={onClose}><i className="iconfont icon-close" /></button></span>
            </div>
            <div className="find-ship-detail-content">
                <h3>船舶档案</h3>
                <div className="find-ship-detail-grid">
                    <DetailItem label="MMSI" value={ship.id} /><DetailItem label="类型" value={SHIP_TYPE.getIntroByCode(ship.type)} />
                    <DetailItem label="IMO" value={ship.imo} /><DetailItem label="呼号" value={ship.callsign} />
                    <DetailItem label="船长/宽" value={`${ship.len || EMPTY_VALUE} / ${ship.wid || EMPTY_VALUE} 米`} /><DetailItem label="吃水" value={ship.dpth} />
                    <DetailItem label="载重吨" value={ship.tonCapacity?.toLocaleString()} /><DetailItem label="净吨" value={ship.tonPure?.toLocaleString()} />
                </div>
                <div className="find-ship-detail-separator" />
                <h3>船位追踪 <small>更新于{ship.ct || EMPTY_VALUE}</small></h3>
                <div className="find-ship-detail-grid">
                    <DetailItem label="当前位置" value={ship.location} wide />
                    <DetailItem label="目的地" value={ship.dest} /><DetailItem label="预到时间" value={ship.eta} />
                    <DetailItem label="状态" value={SHIP_STATUS.getIntroByCode(Number(ship.status))} /><DetailItem label="航速" value={ship.spd} />
                    <DetailItem label="航迹向" value={ship.cog} /><DetailItem label="船艏向" value={ship.hdg} />
                    <DetailItem label="经度" value={ship.lon} /><DetailItem label="纬度" value={ship.lat} />
                </div>
                <div className="find-ship-detail-buttons">
                    <button type="button" onClick={onTrack}><i className="iconfont icon-orbit" />查询轨迹</button>
                    <button type="button" onClick={onFollow}><i className={`iconfont ${ship.isFollowed ? "icon-a-aixin1" : "icon-aixin-1"}`} />{ship.isFollowed ? "取消关注" : "关注"}</button>
                </div>
            </div>
        </div>
    );
}

/** 地图右下角工具栏沿用 Vue toolPanel.vue 的 iconfont 与排列。 */
export function FindShipToolPanel({ green, greenDisabled, satellite, measure, harbor, onMeasure, onGreen, onHarbor, onSatellite }: { green: boolean; greenDisabled?: boolean; satellite: boolean; measure?: boolean; harbor?: boolean; onMeasure?: () => void; onGreen: () => void; onHarbor?: () => void; onSatellite: () => void }) {
    return <div className="find-ship-tool-panel">
        <button className={measure ? "active" : ""} type="button" onClick={onMeasure}><i className="iconfont icon-a-measure1" /><span>测距</span></button>
        <button className={green ? "active" : ""} disabled={greenDisabled} type="button" onClick={onGreen}><i className="iconfont icon-ship" /><span>绿点</span></button>
        <button className={harbor ? "active" : ""} type="button" onClick={onHarbor}><i className="iconfont icon-gangkou" /><span>港口</span></button>
        <button className={satellite ? "active" : ""} type="button" onClick={onSatellite}><i className="iconfont icon-space" /><span>卫星</span></button>
    </div>;
}
